import { expandedGoalsScreenPath } from "@gtmhub/goals/filter-utils";
import { getPersistedScreenSettings, IGridSortingRule, setPersistedScreenSettings } from "@gtmhub/shared/components/grid-filtering-bar";
import { IdMap } from "@gtmhub/util";
import { byBooleanField, byNumericField } from "@gtmhub/util/sorting";
import { IFilterService } from "angular";
import { INestedTreeGoal, INestedTreeMetric, INestedTreeOkrNode, INestedTreeProperties } from "../models";
import { alphanumericComparator, expandedGoalsKey, getPersistedExpandedGoals, resetPersistedExpandedItems, sortingRulesToOrderStrings } from "../utils";

export class NestedTreeService {
  public static $inject = ["$filter"];
  constructor(private $filter: IFilterService) {}

  public constructNestedViewOkrTree(properties: INestedTreeProperties): INestedTreeOkrNode {
    const parentIdMap: IdMap<INestedTreeGoal[]> = {};
    const { goals, sessionId } = properties;
    const firstNodeId = "root";

    goals.forEach((goal) => {
      if (this.isRootNode(sessionId, goal)) {
        goal.parentId = firstNodeId;
      }

      parentIdMap[goal.parentId] = parentIdMap[goal.parentId] || [];
      parentIdMap[goal.parentId].push(goal);
    });

    const fakeRoot: INestedTreeOkrNode = {
      children: [],
      type: firstNodeId,
      isExpanded: true,
      filterMatched: false,
      depth: 0,
    };

    let expandedNodes = getPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedGoalsKey);
    if (!expandedNodes) {
      // If the expanded goal ids from screen settings are not present, we expand root goals by default
      expandedNodes = this.setNestedTreeDefaultView(sessionId, parentIdMap);
    }

    this.fillNestedViewTreeResursive(firstNodeId, fakeRoot, parentIdMap, properties, expandedNodes);

    return fakeRoot;
  }

  private fillNestedViewTreeResursive(
    parentId: string,
    currentNode: INestedTreeOkrNode,
    goalsByParent: IdMap<INestedTreeGoal[]>,
    properties: INestedTreeProperties,
    expandedNodes: IdMap<boolean>
  ): void {
    const { matchedGoalIds, sort, showMetrics } = properties;
    const currentNodeDepth = currentNode.depth + 1;

    if (currentNode.type === "goal" && showMetrics) {
      const metrics = currentNode.goal.metrics ? currentNode.goal.metrics : [];
      metrics.sort(byNumericField("orderId"));

      metrics.forEach((metric) => {
        const metricNode = this.createMetricNode(metric, currentNodeDepth, properties, expandedNodes);
        this.fillNestedViewTreeResursive(metric.id, metricNode, goalsByParent, properties, expandedNodes);
        currentNode.children.push(metricNode);
      });
    }

    const sortedGoals = this.getSortedGoals(goalsByParent[parentId] || [], sort, currentNodeDepth);
    sortedGoals.forEach((goal) => {
      const filterMatched = matchedGoalIds.some((id) => goal.id === id);

      const goalNode = this.createGoalNode(goal, currentNodeDepth, properties, expandedNodes, filterMatched);
      this.fillNestedViewTreeResursive(goal.id, goalNode, goalsByParent, properties, expandedNodes);
      currentNode.children.push(goalNode);
    });
  }

  public toggleExpandItem = (sessionId: string, itemId: string, isExpanded: boolean) => {
    const perssistedItemIds = getPersistedExpandedGoals(sessionId);
    if (isExpanded) {
      perssistedItemIds[itemId] = true;
    } else {
      delete perssistedItemIds[itemId];
    }
    setPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedGoalsKey, perssistedItemIds);
  };

  public expandAllNodes = (sessionId: string, tree: INestedTreeOkrNode) => {
    const expandedItemIds: IdMap<boolean> = {};

    const expand = (node: INestedTreeOkrNode, expandedItems: IdMap<boolean>) => {
      node.children.forEach((child) => {
        expand(child, expandedItemIds);
        const nodeId = child.type === "goal" ? child.goal.id : child.metric.id;
        if (this.isNestedNodeExpandable(child)) {
          child.isExpanded = true;
          expandedItems[nodeId] = true;
        }
      });
    };
    expand(tree, expandedItemIds);
    setPersistedScreenSettings(expandedGoalsScreenPath(sessionId), expandedGoalsKey, expandedItemIds);
  };

  public collapseAllNodes = (sessionId: string, tree: INestedTreeOkrNode) => {
    const collapse = (node: INestedTreeOkrNode) => {
      node.children.forEach((child) => {
        collapse(child);
        child.isExpanded = false;
      });
    };
    collapse(tree);
    resetPersistedExpandedItems(sessionId);
  };

  private setNestedTreeDefaultView(sessionId: string, goalsByParents: IdMap<INestedTreeGoal[]>): IdMap<boolean> {
    const expandedItems = {};
    const rootGoals = goalsByParents["root"];

    if (rootGoals?.length) {
      rootGoals.forEach((rootGoal) => {
        const childGoals = goalsByParents[rootGoal.id];
        const hasChildren = childGoals?.length > 0 || rootGoal.metrics?.length > 0;
        if (hasChildren) {
          expandedItems[rootGoal.id] = true;
        }
      });
    }
    setPersistedScreenSettings(expandedGoalsScreenPath(sessionId), "expandedGoals", expandedItems);
    return expandedItems;
  }

  public isNestedNodeExpandable = (node: INestedTreeOkrNode) => {
    if (node.type === "goal") {
      return node.children?.length > 0 || node.goal.metrics?.length > 0;
    } else {
      return node.children?.length > 0;
    }
  };

  // The goal will be treated as a root node in case it doesn't have a parent, or its parent is from another session
  private isRootNode = (currentSessionId: string, goal: INestedTreeGoal): boolean => {
    if (!goal.parentId) {
      return true;
    }
    // Obfuscated goals don't have parent session id
    if (goal.parentId && !goal.parentGoalSessionId) {
      return false;
    }
    return goal.parentGoalSessionId !== currentSessionId;
  };

  private createMetricNode = (metric: INestedTreeMetric, depth: number, properties: INestedTreeProperties, expandedNodes: IdMap<boolean>): INestedTreeOkrNode => {
    const { filtersApplied } = properties;
    let isExpanded = false;

    if (filtersApplied) {
      isExpanded = true;
    } else {
      isExpanded = expandedNodes[metric.id] || false;
    }

    return {
      children: [],
      depth: depth,
      type: "metric",
      metric: metric,
      isExpanded: isExpanded,
      filterMatched: false,
    };
  };

  private createGoalNode = (
    goal: INestedTreeGoal,
    depth: number,
    properties: INestedTreeProperties,
    expandedNodes: IdMap<boolean>,
    matchesFilter: boolean
  ): INestedTreeOkrNode => {
    const { filtersApplied } = properties;
    let isExpanded = false;

    if (filtersApplied) {
      isExpanded = true;
    } else {
      isExpanded = expandedNodes[goal.id] || false;
    }

    return {
      children: [],
      type: "goal",
      goal: goal,
      depth: depth,
      isExpanded: isExpanded,
      filterMatched: matchesFilter,
    };
  };

  private getSortedGoals = (goals: INestedTreeGoal[], order: IGridSortingRule[], currentDepth: number): INestedTreeGoal[] => {
    if (currentDepth === 1) {
      goals = this.$filter("orderBy")(goals, sortingRulesToOrderStrings(order), false, alphanumericComparator);
    } else {
      goals.sort(byNumericField("orderId"));
    }
    goals.sort(byBooleanField("obfuscated"));
    return goals;
  };
}
